<template>

    <div style="height: 100%; min-height: 300px;" ref="mainContainer">

        <v-card height="100%" flat>
            <v-card-text class="pl-0 pr-0">

                <div class="d-flex">

                    <div class="align-self-center title grey--text text--lighten-2 d-flex">

                        <project-status-box
                                             :value="project.status"
                                             hint="Project status"
                                             :hint-at-top="true">
                        </project-status-box>

                        <div class="align-self-center ml-3">{{ project.name }}</div>

                        <v-tooltip color="primary" top open-delay="500">
                            <template v-slot:activator="{ on }">
                                <v-chip v-on="on" label small class="align-self-center grey--text text--lighten-1 ml-2">{{
                                        project.id
                                    }}
                                </v-chip>
                            </template>
                            Project ID
                        </v-tooltip>
                    </div>

                    <v-scale-transition>
                        <v-alert type="warning" icon="mdi-alert" dense class="body-2 ml-5 mb-0 align-self-center"
                                 v-show="lastRecordedWarning">
                            Last recorded activity: {{ toHumanFrom(lastRecordedActivity) }} or on
                            {{ toLocalDateTime(lastRecordedActivity) }}.

                            <span v-if="project.type==='auto'">
                                This project is out of sample.
                            </span>

                        </v-alert>
                    </v-scale-transition>

                    <div class="flex-grow-1">
                    </div>

                    <v-btn class="align-self-center" icon @click="reloadClicked">
                        <v-icon>mdi-reload</v-icon>
                    </v-btn>

                </div>

                <v-container fluid class="pl-0 pr-0">
                    <v-row dense>

                        <!-- STATUS COUNTS -->
                        <v-col cols="2">
                            <div class="d-flex flex-column">
                                <vertical-status-boxes :style="'height:'+height+'px'" :project="project"></vertical-status-boxes>
                            </div>
                        </v-col>

                        <!-- CHART -->
                        <v-col cols="10">
                            <div class="d-flex flex-column">

                                <div class="flex-grow-1">
                                    <div v-if="!projectStatsLoading && !noData"
                                         :style="'height:'+height+'px'"
                                         class="chart pt-4"
                                         ref="chartdiv">
                                    </div>
                                </div>

                                <!-- CHART LOADER -->
                                <div class="flex-grow-1 mt-10" v-show="projectStatsLoading || noData">
                                    <chart-loader :loading="projectStatsLoading"
                                                  :no-data="noData"
                                                  height="100%"
                                                  width="100%"
                                    ></chart-loader>
                                </div>

                            </div>
                        </v-col>
                    </v-row>
                </v-container>
            </v-card-text>
        </v-card>
    </div>
</template>

<script>

import {timeDisplay} from '../../../../components/mixins/timeDisplayMixin';
import ChartLoader from '../../../../components/UI/ChartLoader';
import {mapState, mapActions} from 'vuex';
import * as am4core from "@amcharts/amcharts4/core";
import * as am4charts from "@amcharts/amcharts4/charts";
import moment from "moment";
import VerticalStatusBoxes from "./VerticalStatusBoxes";
import ProjectStatusBox from "../ProjectStatusBox";
import {statusColorMixin} from "../../../../components/mixins/statusColorMixin";

export default {

    mixins: [timeDisplay, statusColorMixin],

    components: {
        ProjectStatusBox,
        VerticalStatusBoxes,
        ChartLoader
    },

    props: {
        project: {
            type: Object,
            required: true
        }
    },

    data() {
        return {

            chart: null,
            chartsLib: null,
            noData: false,
            lastRecordedActivity: null,
            lastRecordedWarning: false,
            height: null,
            displayNoDataIndicator: false,
        }
    },

    mounted() {
        window.addEventListener('resize', this.handleResize);

        this.$nextTick(() => {
            this.handleResize();
            this.loadData();
        });
    },

    beforeDestroy() {
        if (this.chart) {
            this.chart.dispose();
            window.removeEventListener('resize', this.handleResize);
        }
    },

    computed: {

        ...mapState('ProjectManager', [
            'projectStats',
            'projectStatsLoading',
        ]),

        showReminder() {
            return this.project.reminder_hours > 0;
        },

        projectStatus() {
            return this.project.status ? this.project.status.toUpperCase() : 'Unknown';
        },
    },

    methods: {

        ...mapActions('ProjectManager', [
            'getProject',
            'getProjectStats',
        ]),

        reloadClicked() {
            this.loadData(true);
        },

        handleResize() {

            if (this.$refs.mainContainer.clientHeight) {
                let clientHeight = this.$refs.mainContainer.clientHeight - 70;

                // Maximum height for the chart
                if (clientHeight > 450) clientHeight = 450;

                this.height = clientHeight;
            }
        },

        loadData(force = false) {

            this.noData = false;

            this.getProject({id: this.project.id, force: force});

            this.getProjectStats({forceUpdate: force, projectId: this.project.id})

                .then(activityStats => {

                    if (this.chart) this.chart.dispose();

                    this.createChart(activityStats.data);

                    this.noData = activityStats.data.length < 2;

                    this.lastRecordedActivity = activityStats.last_recorded_activity;

                    if (this.project.status === "online" && this.project.completed_count < this.project.target_completes) {
                        this.lastRecordedWarning = moment(this.lastRecordedActivity).isBefore(moment().subtract(75, 'minutes'));
                    }
                })
                .catch(error => {
                    Bus.$emit('flash-message', {
                        type: 'error',
                        message: 'Project activity statistics failed to load!'
                    });
                });
        },

        getExportName() {
            const date = moment().format('MMMM Do YYYY');
            const name = this.project.name
                .replaceAll(/\W/g, ' ');
            return name + ' ' + date;
        },

        createChart(data) {

            let chart = am4core.create(this.$refs.chartdiv, am4charts.XYChart);

            chart.paddingRight = 20;
            chart.paddingLeft = 0;
            chart.paddingBottom = 10;
            chart.paddingTop = 20;
            chart.height = this.height;
            //chart.responsive.enabled = true;

            chart.cursor = new am4charts.XYCursor();
            chart.dateFormatter.inputDateFormat = "yyyy-MM-dd HH:mm:ss";
            chart.exporting.menu = new am4core.ExportMenu();
            chart.exporting.menu.align = "right";
            chart.exporting.menu.verticalAlign = "top";
            chart.exporting.filePrefix = this.getExportName()

            // These same colors also used in the project dashboard project activity chart
            chart.colors.list = [
                am4core.color(this.statusColors.started),
                am4core.color(this.statusColors.quality),
                am4core.color(this.statusColors.duplicate),
                am4core.color(this.statusColors.security),
                am4core.color(this.statusColors.quota),
                am4core.color(this.statusColors.profile),
                am4core.color(this.statusColors.completed),
                am4core.color(this.statusColors.invited),
                am4core.color(this.statusColors.reminded),
                am4core.color(this.statusColors.opened),
                am4core.color(this.statusColors.routedIn),
                am4core.color(this.statusColors.routedOut),
            ];

            let dateAxis = chart.xAxes.push(new am4charts.DateAxis());

            // Moved column labels in the center of each column
            dateAxis.renderer.grid.template.location = 0;
            // dateAxis.renderer.cellStartLocation = 0.2;
            // dateAxis.renderer.cellEndLocation = 0.9;
            //dateAxis.renderer.labels.template.rotation = 90;

            dateAxis.dateFormatter = new am4core.DateFormatter();
            //categoryAxis.dateFormatter.dateFormat = "MM-dd";
            //dateAxis.dateFormats.setKey("day", "MMM dt");
            dateAxis.periodChangeDateFormats.setKey("hour", "MMM dt");
            //dateAxis.periodChangeDateFormats.setKey("hour", "HH:mm");
            dateAxis.baseInterval = {
                timeUnit: "hour",
                count: 1
            };
            dateAxis.groupData = true;
            dateAxis.groupCount = 24 * 3;
            dateAxis.renderer.minWidth = 100;

            let valueAxis = chart.yAxes.push(new am4charts.ValueAxis());
            valueAxis.renderer.minWidth = 100;
            valueAxis.tooltip.disabled = true;

            let seriesStarted = chart.series.push(new am4charts.ColumnSeries());
            seriesStarted.showOnInit = false;
            seriesStarted.name = "Starts";
            seriesStarted.columns.template.fillOpacity = 0.5;
            seriesStarted.dataFields.valueY = "started";
            seriesStarted.dataFields.dateX = "period";
            seriesStarted.tooltipText = "{name}: [bold]{valueY}[/]";
            seriesStarted.stacked = false;

            let seriesQuality = chart.series.push(new am4charts.ColumnSeries());
            seriesQuality.showOnInit = false;
            seriesQuality.dataFields.valueY = "quality";
            seriesQuality.dataFields.dateX = "period";
            seriesQuality.tooltipText = "{valueY.value}";
            seriesQuality.columns.template.fillOpacity = 0.5;
            seriesQuality.name = "Quality terminated";
            seriesQuality.tooltipText = "{name}: [bold]{valueY}[/]";
            seriesQuality.stacked = true;
            seriesQuality.tooltip.label.adapter.add("text", function (text, target) {
                return target.dataItem && target.dataItem.valueY === 0 ? "" : text
            });

            let seriesDuplicates = chart.series.push(new am4charts.ColumnSeries());
            seriesDuplicates.showOnInit = false;
            seriesDuplicates.dataFields.valueY = "duplicate";
            seriesDuplicates.dataFields.dateX = "period";
            seriesDuplicates.tooltipText = "{valueY.value}";
            seriesDuplicates.columns.template.fillOpacity = 0.5;
            seriesDuplicates.name = "Duplicate count";
            seriesDuplicates.tooltipText = "{name}: [bold]{valueY}[/]";
            seriesDuplicates.stacked = true;
            seriesDuplicates.tooltip.label.adapter.add("text", function (text, target) {
                return target.dataItem && target.dataItem.valueY === 0 ? "" : text
            });

            let seriesSecurity = chart.series.push(new am4charts.ColumnSeries());
            seriesSecurity.showOnInit = false;
            seriesSecurity.dataFields.valueY = "security";
            seriesSecurity.dataFields.dateX = "period";
            seriesSecurity.tooltipText = "{valueY.value}";
            seriesSecurity.columns.template.fillOpacity = 0.5;
            seriesSecurity.name = "Security count";
            seriesSecurity.tooltipText = "{name}: [bold]{valueY}[/]";
            seriesSecurity.stacked = true;
            seriesSecurity.tooltip.label.adapter.add("text", function (text, target) {
                return target.dataItem && target.dataItem.valueY === 0 ? "" : text
            });

            let seriesQuota = chart.series.push(new am4charts.ColumnSeries());
            seriesQuota.showOnInit = false;
            seriesQuota.dataFields.valueY = "quota";
            seriesQuota.dataFields.dateX = "period";
            seriesQuota.tooltipText = "{valueY.value}";
            seriesQuota.columns.template.fillOpacity = 0.5;
            seriesQuota.name = "Quota terminated";
            seriesQuota.tooltipText = "{name}: [bold]{valueY}[/]";
            seriesQuota.stacked = true;
            seriesQuota.tooltip.label.adapter.add("text", function (text, target) {
                return target.dataItem && target.dataItem.valueY === 0 ? "" : text
            });

            let seriesProfile = chart.series.push(new am4charts.ColumnSeries());
            seriesProfile.showOnInit = false;
            seriesProfile.dataFields.valueY = "profile";
            seriesProfile.dataFields.dateX = "period";
            seriesProfile.columns.template.fillOpacity = 0.5;
            seriesProfile.name = "Profile terminated";
            seriesProfile.tooltipText = "{name}: [bold]{valueY}[/]";
            seriesProfile.stacked = true;
            seriesProfile.tooltip.label.adapter.add("text", function (text, target) {
                return target.dataItem && target.dataItem.valueY === 0 ? "" : text
            });

            let seriesCompleted = chart.series.push(new am4charts.ColumnSeries());
            seriesCompleted.showOnInit = false;
            seriesCompleted.dataFields.valueY = "completed";
            seriesCompleted.dataFields.dateX = "period";
            seriesCompleted.columns.template.fillOpacity = 0.5;
            seriesCompleted.name = "Completed";
            seriesCompleted.tooltipText = "{name}: [bold]{valueY}[/]";
            seriesCompleted.stacked = true;

            let seriesInvited = chart.series.push(new am4charts.LineSeries());
            seriesInvited.showOnInit = false;
            seriesInvited.name = "Invited";
            seriesInvited.tensionY = 0.9;
            seriesInvited.tensionX = 0.9;
            seriesInvited.dataFields.valueY = "invited";
            seriesInvited.dataFields.dateX = "period";
            seriesInvited.tooltipText = "{name}: [bold]{valueY}[/]";
            seriesInvited.stacked = false;
            seriesInvited.strokeWidth = 2;

            let seriesReminded = chart.series.push(new am4charts.LineSeries());
            seriesReminded.showOnInit = false;
            seriesReminded.name = "Reminded";
            seriesReminded.tensionY = 0.9;
            seriesReminded.tensionX = 0.9;
            seriesReminded.dataFields.valueY = "reminded";
            seriesReminded.dataFields.dateX = "period";
            seriesReminded.tooltipText = "{name}: [bold]{valueY}[/]";
            seriesReminded.stacked = false;
            seriesReminded.strokeWidth = 2;
            seriesReminded.tooltip.label.adapter.add("text", function (text, target) {
                return target.dataItem && target.dataItem.valueY === 0 ? "" : text
            });

            let seriesOpened = chart.series.push(new am4charts.LineSeries());
            seriesOpened.showOnInit = false;
            seriesOpened.name = "Opened";
            seriesOpened.tensionY = 0.9;
            seriesOpened.tensionX = 0.9;
            seriesOpened.dataFields.valueY = "opened";
            seriesOpened.dataFields.dateX = "period";
            seriesOpened.tooltipText = "{name}: [bold]{valueY}[/]";
            seriesOpened.stacked = false;
            seriesOpened.strokeWidth = 2;
            seriesOpened.strokeOpacity = 1;
            seriesOpened.tooltip.label.adapter.add("text", function (text, target) {
                return target.dataItem && target.dataItem.valueY === 0 ? "" : text
            });

            let seriesRoutedIn = chart.series.push(new am4charts.LineSeries());
            seriesRoutedIn.showOnInit = false;
            seriesRoutedIn.name = "Routed in";
            seriesRoutedIn.tensionY = 0.9;
            seriesRoutedIn.tensionX = 0.9;
            seriesRoutedIn.dataFields.valueY = "routed_in";
            seriesRoutedIn.dataFields.dateX = "period";
            seriesRoutedIn.tooltipText = "{name}: [bold]{valueY}[/]";
            seriesRoutedIn.stacked = false;
            seriesRoutedIn.strokeWidth = 1.5;
            seriesRoutedIn.strokeDasharray = [8, 4];
            seriesRoutedIn.strokeOpacity = 1;
            seriesRoutedIn.tooltip.label.adapter.add("text", function (text, target) {
                return target.dataItem && target.dataItem.valueY === 0 ? "" : text
            });

            let seriesRoutedOut = chart.series.push(new am4charts.LineSeries());
            seriesRoutedOut.showOnInit = false;
            seriesRoutedOut.name = "Routed out";
            seriesRoutedOut.tensionY = 0.9;
            seriesRoutedOut.tensionX = 0.9;
            seriesRoutedOut.dataFields.valueY = "routed_out";
            seriesRoutedOut.dataFields.dateX = "period";
            seriesRoutedOut.tooltipText = "{name}: [bold]{valueY}[/]";
            seriesRoutedOut.stacked = false;
            seriesRoutedOut.strokeWidth = 1.5;
            seriesRoutedOut.strokeDasharray = [8, 4];
            seriesRoutedOut.strokeOpacity = 1;
            seriesRoutedOut.tooltip.label.adapter.add("text", function (text, target) {
                return target.dataItem && target.dataItem.valueY === 0 ? "" : text
            });

            chart.data = data;

            this.chart = chart;
        },
    }
}
</script>

<style>

.amcharts-amexport-item {
    border: 2px solid #777;
}

.amcharts-amexport-label {
    color: white !important;
}

</style>
